/**
 * The trainer's rating of one draft (R47): two thumbs, and once one is chosen an optional comment.
 *
 * Voluntary at every step: nothing is sent until a thumb is pressed, and the comment is a second,
 * separate send that carries the rating it belongs to. The chosen thumb is the server's answer
 * (`feedback`); the comment's text and whether it went are this card's own, until it is sent.
 */

import { useId, useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import hidden from '~/shared/ui/visuallyHidden.module.css';

import { ThumbGlyph } from './ChatGlyphs';
import surfaces from './surfaces.module.css';
import styles from './DraftFeedback.module.css';

/** The backend's limit on a feedback comment (`AiFeedbackRequest.Comment`). */
const COMMENT_MAX_LENGTH = 2000;

interface DraftFeedbackProps {
  /** The rating as the server holds it: `true` helpful, `false` not, `null` unrated. */
  readonly feedback: boolean | null;
  /** Send the rating, with a comment or without; answers whether the server took it. */
  readonly onRate: (helpful: boolean, comment: string | null) => Promise<boolean>;
  /** What is rated: a draft, or a proposal (R47); the question names it. */
  readonly subject: 'draft' | 'proposal';
}

export function DraftFeedback({ feedback, onRate, subject }: DraftFeedbackProps) {
  const { t } = useTranslation();
  const fieldId = useId();
  const reasonId = useId();
  const [comment, setComment] = useState('');
  const [sent, setSent] = useState(false);
  // One send at a time: a second press while the first is on its way sends nothing.
  const [sending, setSending] = useState(false);
  const words = comment.trim();
  const refusal = words === '' ? t('web:aiPlan.feedback.commentEmpty') : null;

  const thumb = (helpful: boolean) => (
    <button
      type="button"
      className={clsx(styles.thumb, feedback === helpful && styles.thumbOn)}
      aria-pressed={feedback === helpful}
      aria-label={t(helpful ? 'web:aiPlan.feedback.helpful' : 'web:aiPlan.feedback.notHelpful')}
      onClick={() => {
        void onRate(helpful, null);
      }}
    >
      <ThumbGlyph className={clsx(styles.thumbGlyph, !helpful && styles.thumbDown)} />
    </button>
  );

  return (
    <>
      <div className={styles.feedback} role="group" aria-label={t(subject === 'draft' ? 'web:aiPlan.feedback.label' : 'web:aiPlan.feedback.labelProposal')}>
        {thumb(true)}
        {thumb(false)}
      </div>
      {feedback !== null && !sent && (
        <form
          className={styles.comment}
          onSubmit={(event) => {
            event.preventDefault();
            if (refusal !== null || sending) return;
            setSending(true);
            void onRate(feedback, words).then((taken) => {
              setSending(false);
              if (taken) setSent(true);
            });
          }}
        >
          <label htmlFor={fieldId} className={hidden.srOnly}>
            {t('web:aiPlan.feedback.commentLabel')}
          </label>
          <input
            id={fieldId}
            className={styles.commentField}
            type="text"
            value={comment}
            maxLength={COMMENT_MAX_LENGTH}
            placeholder={t('web:aiPlan.feedback.commentPlaceholder')}
            onChange={(event) => {
              setComment(event.target.value);
            }}
          />
          <button
            type="submit"
            className={clsx(surfaces.button, surfaces.small)}
            aria-disabled={refusal !== null || sending ? 'true' : undefined}
            aria-describedby={refusal === null ? undefined : reasonId}
          >
            {t('web:aiPlan.feedback.commentSend')}
          </button>
          <span id={reasonId} className={hidden.srOnly}>
            {refusal ?? ''}
          </span>
        </form>
      )}
      {sent && (
        <p className={styles.thanks} role="status">
          {t('web:aiPlan.feedback.commentSent')}
        </p>
      )}
    </>
  );
}
